/**
 * A hastily thrown together variant of the Entry page meant to be used when "quick" input
 * needs to be captured and don't want to burden the user with the full Fletch workflow details
 */
import React, {Dispatch, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useParams, useHistory} from 'react-router-dom'
import Sticky from 'react-stickynode';
import ReactSelect from 'react-select/async';

import {Choice, Field, StatusMessage} from '../types';

import {Form, FormState, initialFieldState, initialFormState} from '../components/Form';
import {LoadingIndicator} from '../components/LoadingIndicator';
import {EntryComments} from '../components/EntryComments';
import {EntryTimeline} from '../components/EntryTimeline';
import {EntryWorkflowStatus} from '../components/EntryWorkflowStatus';
import {EntryAdminActions} from '../components/EntryAdminActions';
import {Modal} from '../components/Modal';
import {FormContainer} from '../components/FormContainer';
import {ActivityButton} from '../components/ActivityButton';

import {calculationFieldValues, fieldValue, formIsBusy, validate} from '../_helpers/form';
import {Flow, FlowFetchResponse} from '../_services/Flow';
import {Etl} from '../_services/Etl';
import {postStatusMessageWithTimeout} from '../_actions/statusMessage';
import {User} from '../_services/User';
import {AdminActionCommentModal} from '../components/AdminActionCommentModal';
import {WorkflowDiagram} from '../components/WorkflowDiagram';

const debounce = require('debounce-promise');

type SimpleEntryProps = {
    dispatchStatusMessage: (message: StatusMessage) => void;
};

type AdminActionCommentModalState = {
    show: boolean;
    action?: Choice;
};
const initialAdminActionModalState = {
    show: false,
    action: undefined
};

const SimpleEntry: React.FunctionComponent<SimpleEntryProps> = ({
                                                        dispatchStatusMessage
                                                    }): JSX.Element => {

    const {entryId} = useParams();
    const history = useHistory();

    const [formState, setFormState] = useState<FormState>({
        ...initialFormState,
        fieldValues: {}
    });
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const isDirtyRef = React.useRef(isDirty);

    const [fieldStateUpdateCount, setFieldStateUpdateCount] = useState<number>(0);

    const [entry, setEntry]: any = useState({});
    const [entryIsLoading, setEntryIsLoading]: any = useState(false);
    const [entryLoadErrorMessage, setEntryLoadErrorMessage]: any = useState('');

    const [statusIsUpdating, setStatusIsUpdating] = useState<string>('');
    const [statusUpdateErrorMessage, setStatusUpdateErrorMessage] = useState<string>('');

    // this is used to trigger the useEffect hook to update the entry
    // not a great approach and needs to be updated
    const [entryRefreshCounter, setEntryRefreshCounter] = useState<number>(1);

    const [entryNote, setEntryNote] = useState<string>('');
    const [entryNoteErrorMessage, setEntryNoteErrorMessage] = useState<string>('');

    const [showAdminAddAssigneeModal, setShowAdminAddAssigneeModal] = useState<boolean>(false);
    const [adminAdditionalAssignees, setAdminAdditionalAssignees] = useState<Choice[]>([]);
    const [adminAddAssigneeComment, setAdminAddAssigneeComment] = useState<string>('');

    const [adminActionModalState, setAdminActionModalState] = useState<AdminActionCommentModalState>(initialAdminActionModalState);

    const [userChangeStepErrorMessage, setUserChangeStepErrorMessage] = useState<string>('');
    const [showUserChangeStepConfirmationModal, setShowUserChangeStepConfirmationModal] = useState<boolean>(false);

    const [showTransferModal, setShowTransferModal] = useState<boolean>(false);
    const [transferAssignees, setTransferAssignees] = useState<Choice[]>([])
    const [transferAssigneesReplaceCurrent, setTransferAssigneesReplaceCurrent] = useState<boolean>(false);

    const [printAllDetails, setPrintAllDetails] = useState<boolean>(User.getInstance().getPrintAllDetails());

    useEffect(() => {
        setEntryIsLoading(true);
        setEntryLoadErrorMessage('');

        // todo - implement abort - https://robwise.github.io/blog/cancel-whatwg-fetch-requests-in-react
        Flow.getInstance().getEntry(entryId)
            .then((response: FlowFetchResponse) => {
                if (response.ok && response.json.entry) {
                    // define form state
                    const state = {
                        ...formState,
                        fieldState: {...initialFieldState},
                        fieldValues: {
                            ...response.json.entry,
                        }
                    };

                    // process any calculation fields
                    const calculatedFields = calculationFieldValues(response.json.form, state, response.json.form.calculationFields);

                    // set entry and formState
                    setEntry(response.json);
                    setFormState({
                        ...state,
                        fieldValues: {
                            ...state.fieldValues,
                            ...calculatedFields
                        }
                    });
                } else {
                    setEntryLoadErrorMessage(response.json.message || 'Error loading entry');
                }
                setEntryIsLoading(false);
            })
            .catch((error: any) => {
                setEntryLoadErrorMessage(error || 'Exception loading entry');
                setEntryIsLoading(false);
            });
    }, [entryId, entryRefreshCounter]);

    useEffect(() => {
        window.addEventListener('beforeunload', warnBeforeLeave)
        return () => {
            window.removeEventListener('beforeunload', warnBeforeLeave)
        }
    }, [])

    useEffect(() => {
        setFieldStateUpdateCount((n: number) => n + 1);

        // skip the first couple of state updates,
        // this is far from ideal, but good enough for now
        if (fieldStateUpdateCount > 1) {
            setIsDirty(true);
            isDirtyRef.current = true;
        }
    }, [formState.fieldValues]);

    const warnBeforeLeave = (e) => {
        if (isDirtyRef.current) {
            e.preventDefault();
            e.returnValue = '';
        } else {
            delete e['returnValue'];
        }
    }

    /**
     *
     * @param status new workflow status
     * @param requireValidation whether the form should be validated before performing status update
     * @param data any associated data with the request
     */
    const processStatusUpdate = (status: string, requireValidation: boolean, data: any = {}) => () => {
        setEntryNoteErrorMessage('');
        setStatusUpdateErrorMessage('');

        console.log('setting isDirty false', isDirty);
        setIsDirty(false);
        isDirtyRef.current = false;

        // NOTE - not ideal, needs to be rewritten for validation logic to be a part of configuration
        // tslint:disable-next-line:triple-equals
        if (entry.form.id == '232' && status === 'rejected') {
            const rejectReason = fieldValue(formState, entry.form.fieldMap['79']);
            if (!rejectReason || rejectReason?.length < 1) {
                alert('You must provide Reason for Rejection');

                const fieldName = entry.form.fieldMap['79'].name;
                setFormState({
                    ...formState,
                    fieldState: {
                        ...formState.fieldState,
                        [fieldName]: {
                            ...formState.fieldState[fieldName],
                            scrollTo: true,
                            invalid: true,
                        }
                    }
                });

                return;
            }
        }

        if (entry.step?.flowAdvancedOptions?.confirmations?.hasOwnProperty(status) && entry.step.flowAdvancedOptions.confirmations[status]?.show) {
            const message = entry.step.flowAdvancedOptions.confirmations[status]?.message || 'Are you sure?';

            if (!confirm(message)) {
                console.log('cancelled confirmation');
                return;
            }
        }

        let hasValidationError = false;
        // there's a bug where if a step has no editable fields and there is a require
        // administrative field on the form that isn't visible it throws a validation error
        // by adding at least 1 entry to entry.step.editableFields that
        // bug is fixed
        // NOTE: once proper tests are written around the validate function, I'll feel more
        // comfortable in fixing this
        const editableFields = [
            ...(entry.step.editableFields.length === 0 ? [''] : entry.step.editableFields),
        ];

        if (requireValidation && !validate(entry.form, formState, setFormState, editableFields)) {
            hasValidationError = true;

            // scroll to first field with validation error
            entry.form.fields.some((field: Field) => {
                const fieldName = field.name;
                if (formState.fieldState.hasOwnProperty(fieldName) && formState.fieldState[fieldName].invalid === true) {
                    setFormState({
                        ...formState,
                        fieldState: {
                            ...formState.fieldState,
                            [fieldName]: {
                                ...formState.fieldState[fieldName],
                                scrollTo: true
                            }
                        }
                    });
                    return true;
                }
                return false;
            });
        }

        // validate note mode
        if (
            (entry.step.noteMode === 'required_if_approved' && status === 'approved' && !entryNote) ||
            (entry.step.noteMode === 'required_if_rejected' && status === 'rejected' && !entryNote) ||
            (entry.step.noteMode === 'required_if_reverted' && status === 'revert' && !entryNote) ||
            (entry.step.noteMode === 'required_if_reverted_or_rejected' && (status === 'revert' || status === 'rejected') && !entryNote)
        ) {
            hasValidationError = true;
            setEntryNoteErrorMessage('A note is required');
        }

        if (hasValidationError) {
            return;
        }

        setStatusIsUpdating(status);
        Flow.getInstance().update(
            entry.form.id,
            status,
            {
                ...formState.fieldValues,
                ['__gravityflow_note']: entryNote,
                ...data
            })
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    window.scrollTo(0, 0);

                    dispatchStatusMessage({
                        type: 'success',
                        message: `Workflow Entry #${entry.id} updated`,
                        icon: 'check-circle'
                    });

                    if (
                        !['take_ownership', 'pending', 'in_progress'].includes(status) && /* only go to Inbox when status update results in a change of step */
                        entry.step.navigateToPageAfterUpdate === 'inbox'
                    ) {
                        history.push('/inbox');
                    } else {
                        setEntryRefreshCounter(entryRefreshCounter + 1);
                    }
                } else {
                    setStatusUpdateErrorMessage(response.json.message || 'Error updating workflow status');
                }
                setEntryNote('');
                setStatusIsUpdating('');
            })
            .catch((error: any) => {
                setStatusUpdateErrorMessage(((error instanceof Object && error.hasOwnProperty('message') ? error.message : error) || 'Exception updating workflow status'));
                setStatusIsUpdating('');
            });
    };

    const handleAdminAction = (actionChoice: Choice, data: any = null): void => {
        const action = actionChoice.value;
        if (action === 'add_assignees' && data === null) {
            setShowAdminAddAssigneeModal(true);
            return;
        } else if (data === null) {
            setAdminActionModalState({
                ...adminActionModalState,
                show: true,
                action: actionChoice
            });
            return;
        }

        setStatusIsUpdating('admin');
        Flow.getInstance().adminAction(entry.id, action, data)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setEntryRefreshCounter(entryRefreshCounter + 1);
                    window.scrollTo(0, 0);
                } else {
                    setStatusUpdateErrorMessage(response.json.message || 'Error updating workflow status');
                }
                setStatusIsUpdating('');
            })
            .catch((error: any) => {
                setStatusUpdateErrorMessage(((error instanceof Object && error.hasOwnProperty('message') ? error.message : error) || 'Exception updating workflow status'));
                setStatusIsUpdating('');

            });
    };

    const handleUserChangeStep = (fromModal: boolean = false): void => {

        // if there is confirmation text and this call isnt coming from the confirmation modal,
        // show the modal and bail
        if (entry.step.userChangeStepOptions.confirmationText && !fromModal) {
            setShowUserChangeStepConfirmationModal(true);
            return;
        }

        setStatusIsUpdating('userChangeStep');
        setUserChangeStepErrorMessage('');

        Flow.getInstance().userChangeStep(entry.id)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setEntryRefreshCounter(entryRefreshCounter + 1);
                    window.scrollTo(0, 0);
                } else {
                    setUserChangeStepErrorMessage(response.json.message || 'Error updating workflow status');
                }
                setStatusIsUpdating('');
            })
            .catch((error: any) => {
                setUserChangeStepErrorMessage(((error instanceof Object && error.hasOwnProperty('message') ? error.message : error) || 'Exception updating workflow status'));
                setStatusIsUpdating('');
            });
    };

    const handleCommentsSaved = () => {
        setEntryRefreshCounter(entryRefreshCounter + 1);
    };

    const handleEmailLookup = debounce(
        Etl.getInstance().employeeAutocompleter,
        250
    );

    const disableActivityButtons = formIsBusy(formState);

    // @ts-ignore
    return (
        <>
            {entry && entry.form &&
            <div className="row no-gutters" style={{margin: '0 -15px'}}>
                <div className="col-12 text-right">
                    <WorkflowDiagram workflowId={entry.form.id} entryId={entryId}/>
                </div>
            </div>
            }
            <div className="row">
                <div className="col-12">
                    {entryLoadErrorMessage && <p className="alert alert-danger">{entryLoadErrorMessage}</p>}
                    {entryIsLoading && <LoadingIndicator/>}

                    {entry.id && <h1>{entry.form.title}</h1>}
                    {entry?.step?.status && <h2 className="step-name"><span>Step - </span>{entry.step.name}</h2>}
                    {entry?.id && <h2 className="entry-id"><span>Entry ID - </span>{entry.id}</h2>}

                    {entry.id &&
                    <div className={'row' + (printAllDetails ? ' print-all-details' : '')}>
                        <div id="workflow-form-container-col" className="col-12 col-sm-8 offset-sm-2">
                            {entry.step.instructions && entry.step.userCanUpdate &&
                            <div className="entry-instructions alert alert-warning"
                                 dangerouslySetInnerHTML={{__html: entry.step.instructions}}
                            />}
                            <FormContainer>
                                <Form form={entry.form}
                                      displayOnly={true}
                                      editableFieldIds={entry.step.editableFields}
                                      highlightEditableFields={entry.step.highlightEditableFields}
                                      displayFields={entry.step.displayFieldsSelected}
                                      displayFieldsMode={entry.step.displayFieldsMode}
                                      formState={formState}
                                      setFormState={setFormState}
                                      showAdministrativeFields={true}
                                      showPrivate={entry.step?.canViewPrivate || false}
                                      step={entry.step}
                                />
                            </FormContainer>

                            <EntryWorkflowStatus
                                entry={entry}
                                statusUpdateErrorMessage={statusUpdateErrorMessage}
                                entryNote={entryNote}
                                setEntryNote={setEntryNote}
                                entryNoteErrorMessage={entryNoteErrorMessage}
                                setEntryNoteErrorMessage={setEntryNoteErrorMessage}
                                statusIsUpdating={statusIsUpdating}
                                processStatusUpdate={processStatusUpdate}
                                disableActivityButtons={disableActivityButtons}
                                setShowTransferModal={setShowTransferModal}
                            />

                        </div>
                    </div>
                    }
                    {showTransferModal &&
                    <Modal>
                        <div className="modal-header">
                            <h5 className="modal-title">Transfer Ownership to Another Assignee</h5>
                            <button type="button"
                                    className="close"
                                    tabIndex={-1}
                                    onClick={() => {
                                        setShowTransferModal(false);
                                        setTransferAssigneesReplaceCurrent(false);
                                        setTransferAssignees([]);
                                    }}
                            >×
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Search for assignees by email. Add one or more assignees then click
                                <em>Transfer to Assignee(s)</em> to transfer the workflow.</p>

                            <div className="form-group">
                                <ReactSelect
                                    isMulti={true}
                                    value={transferAssignees}
                                    onChange={(option: any) => {
                                        setTransferAssignees(option);
                                    }}
                                    placeholder="Enter an email address"
                                    loadOptions={handleEmailLookup}
                                />
                            </div>
                            <div className="form-group">
                                <div className="form-check">
                                    <input className="form-check-input"
                                           type="checkbox"
                                           checked={transferAssigneesReplaceCurrent}
                                           onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                               setTransferAssigneesReplaceCurrent(e.target.checked);
                                           }}
                                           id="transferReplaceCurrent"
                                    />
                                    <label className="form-check-label" htmlFor="transferReplaceCurrent">
                                        Selected Assignee(s) should replace me
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        processStatusUpdate('transfer', false, {
                                            __transferToAssignees: transferAssignees,
                                            __transferToReplaceCurrent: transferAssigneesReplaceCurrent
                                        })();
                                        setTransferAssignees([]);
                                        setTransferAssigneesReplaceCurrent(false);
                                        setShowTransferModal(false);
                                    }}
                            >Transfer to Assignee(s)
                            </button>
                        </div>

                    </Modal>
                    }
                    {showAdminAddAssigneeModal &&
                    <Modal>
                        <div className="modal-header">
                            <h5 className="modal-title">Add Assignee(s) to Current Step</h5>
                            <button type="button"
                                    className="close"
                                    tabIndex={-1}
                                    onClick={() => {
                                        setShowAdminAddAssigneeModal(false);
                                        setAdminAddAssigneeComment('');
                                    }}
                            >×
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Search for assignees by email. Add one or more assignees then click
                                <em>Add Assignee(s)</em> to add to the current step.</p>

                            <div className="form-group">
                                <ReactSelect
                                    isMulti={true}
                                    value={adminAdditionalAssignees}
                                    onChange={(option: any) => {
                                        setAdminAdditionalAssignees(option);
                                    }}
                                    placeholder="Enter an email address"
                                    loadOptions={handleEmailLookup}
                                />
                            </div>
                            <div className="form-group">
                                <p>Enter any notes / comments you want to attach to this admin activity</p>
                                <textarea
                                    className="form-control"
                                    value={adminAddAssigneeComment}
                                    onChange={(e) => {
                                        setAdminAddAssigneeComment(e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        handleAdminAction({value: 'add_assignees', text: 'Add Assignee(s)', label: 'Add Assignee(s)'}, {
                                            assignees: adminAdditionalAssignees,
                                            comments: adminAddAssigneeComment
                                        });
                                        setAdminAdditionalAssignees([]);
                                        setShowAdminAddAssigneeModal(false);
                                        setAdminAddAssigneeComment('');
                                    }}
                            >Add Assignee(s)
                            </button>
                        </div>

                    </Modal>
                    }

                    <AdminActionCommentModal
                        show={adminActionModalState.show}
                        action={adminActionModalState.action}
                        onCloseCancel={() => {
                            setAdminActionModalState({
                                ...initialAdminActionModalState
                            });
                        }}
                        onCloseOk={(action, comments) => {
                            handleAdminAction(action, {comments});
                            setAdminActionModalState({
                                ...initialAdminActionModalState
                            });
                        }}
                    />

                    {showUserChangeStepConfirmationModal && <Modal>
                        <div className="modal-body"
                             style={{padding: '0px'}}
                             dangerouslySetInnerHTML={{__html: entry.step.userChangeStepOptions.confirmationText}}
                        />
                        <div className="modal-footer"
                             style={{padding: '.5rem 1rem'}}
                        >
                            <button type="button"
                                    className="btn btn-sm btn-light"
                                    onClick={() => {
                                        setShowUserChangeStepConfirmationModal(false);
                                    }}
                            >Cancel
                            </button>
                            <button type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() => {
                                        setShowUserChangeStepConfirmationModal(false);
                                        handleUserChangeStep(true);
                                    }}
                            >Continue
                            </button>
                        </div>
                    </Modal>}
                </div>
            </div>
        </>
    );
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
    return {
        dispatchStatusMessage: (message: StatusMessage) => {
            dispatch(postStatusMessageWithTimeout(message, 5000));
        }
    };
};

const ConnectedEntry = connect(null, mapDispatchToProps)(SimpleEntry);
export {ConnectedEntry as SimpleEntry};
